// Thin haptics wrapper. Fires real haptics only inside the Capacitor app
// with @capacitor/haptics available (shipped from the Aug 2026 store release
// onwards); everywhere else - web, older app shells - every call is a silent
// no-op, so call sites never need to guard.
//
// Usage: hapticTick() on small selections (tab change, toggle, carousel
// snap); hapticImpact() on meaningful moments (booking confirmed, stamp
// earned). Keep ticks sparing: haptics that fire on every scroll frame feel
// like a buzzing phone, not a premium one.
import { Capacitor } from '@capacitor/core';

let hapticsModule = null;
let loadFailed = false;

async function getHaptics() {
    if (loadFailed || !Capacitor.isNativePlatform()) return null;
    if (!hapticsModule) {
        try {
            hapticsModule = await import('@capacitor/haptics');
        } catch (err) {
            // Old shell without the plugin - stay silent forever.
            loadFailed = true;
            return null;
        }
    }
    return hapticsModule;
}

/** Light selection tick - tab switches, toggles, carousel snaps. */
export async function hapticTick() {
    const mod = await getHaptics();
    if (!mod) return;
    try {
        await mod.Haptics.impact({ style: mod.ImpactStyle.Light });
    } catch (err) {
        // Plugin present but call rejected (e.g. web platform) - ignore.
    }
}

/** Medium impact - confirmations and rewarding moments. */
export async function hapticImpact() {
    const mod = await getHaptics();
    if (!mod) return;
    try {
        await mod.Haptics.impact({ style: mod.ImpactStyle.Medium });
    } catch (err) {
        // ignore
    }
}

/** Success notification pattern - booking confirmed, reward unlocked. */
export async function hapticSuccess() {
    const mod = await getHaptics();
    if (!mod) return;
    try {
        await mod.Haptics.notification({ type: mod.NotificationType.Success });
    } catch (err) {
        // ignore
    }
}

/** Error notification pattern - a move or cancel that did not go through. */
export async function hapticError() {
    const mod = await getHaptics();
    if (!mod) return;
    try {
        await mod.Haptics.notification({ type: mod.NotificationType.Error });
    } catch (err) {
        // ignore
    }
}
